/**
 * The brand intro clock — one shared answer to "has the logo finished drawing
 * yet?", so every exit from a brand screen can wait for it.
 *
 * The intro is pure CSS (see styles/brand-screen.css), which means nothing in
 * JavaScript observes it directly. Instead the animated `BrandScreen` stamps
 * the moment it mounts, and anything that would tear the page down — the
 * splash dismissing, or an MSAL full-page auth redirect — asks how much of the
 * sequence is left and waits that out first. Module state, not React state:
 * the waiters (AuthProvider's redirects) are outside the splash's tree, and
 * the clock has to survive the splash unmounting.
 */

// The one-shot sequence ends at 4.0s ("MIND" at 3.54s + its 0.46s reveal —
// see docs/16-ui-design-system.md, Loading Screen); hold the assembled logo
// for 0.65s afterwards so it reads as a finished mark, not a cut.
export const INTRO_TOTAL_MS = 4650;
// Reduced motion draws nothing — the assembled mark is there on the first
// frame — so this is only enough of a beat that the screen isn't a flash.
export const REDUCED_MOTION_MIN_MS = 450;

let startedAt: number | null = null;
let durationMs = 0;

/** Called by the animated BrandScreen as it mounts — the CSS animation and
 * this clock start on the same commit. */
export function markBrandIntroStart(): void {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  startedAt = Date.now();
  durationMs = reduced ? REDUCED_MOTION_MIN_MS : INTRO_TOTAL_MS;
}

/** Milliseconds still owed to the intro; 0 when it has finished, and 0 when no
 * animated brand screen ever rendered (a redirect from inside the workspace
 * must not be delayed by an animation that isn't on screen). */
export function brandIntroRemainingMs(): number {
  if (startedAt === null) return 0;
  return Math.max(0, durationMs - (Date.now() - startedAt));
}

/** Resolves once the intro has fully played out — immediately if it already
 * has. Bounded by INTRO_TOTAL_MS, so it can never strand a caller. */
export function waitForBrandIntro(): Promise<void> {
  const remaining = brandIntroRemainingMs();
  if (remaining === 0) return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, remaining));
}

/** Test seam: forget any recorded intro. */
export function resetBrandIntroForTests(): void {
  startedAt = null;
  durationMs = 0;
}
